import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useInventoryState } from '../../../../contexts/inventory.context';
import { useTranslation } from 'src/hooks/useTranslationHook';

const InventoryCategoryDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedCategory } = useInventoryState();

  if (!selectedCategory) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="inventory-category-details-container"
      >
        <Typography>
          {translateText('administration.inventory.noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="inventory-category-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.inventory.columns.categoryName')}:{' '}
        {selectedCategory.name}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('common.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} data-testid="category-description">
              <Typography variant="caption" color="textSecondary">
                {translateText('common.description')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCategory.description}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="category-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.inventory.columns.status')}
              </Typography>
              <Chip
                label={
                  selectedCategory.isActive
                    ? translateText('administration.inventory.status.active')
                    : translateText('administration.inventory.status.inactive')
                }
                color={selectedCategory.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Statistics
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="category-total-items">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.totalItems')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCategory.totalItems}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="category-total-value">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.totalValue')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedCategory.totalValue.toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedCategory.parentCategoryId && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Parent Category
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} data-testid="category-parent-id">
                <Typography variant="caption" color="textSecondary">
                  Parent Category ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCategory.parentCategoryId}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InventoryCategoryDetailsView;
